import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const DAY_ABBR = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat' };

function formatDays(days) {
  const arr = Array.isArray(days) ? days : JSON.parse(days || '[]');
  return arr.map(d => DAY_ABBR[d] || d).join(' · ');
}
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'pm' : 'am'}`;
}

export default function ChooseCohortPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { addToast } = useToast();

  const level = user?.student?.current_level || 'A1';

  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);
  const [confirm, setConfirm] = useState(null); // cohort being confirmed

  useEffect(() => {
    API.get(`/cohorts/available/${level}`)
      .then(({ data }) => setCohorts(data.cohorts || []))
      .catch(() => addToast('Failed to load cohorts', 'error'))
      .finally(() => setLoading(false));
  }, [level]);

  const handleJoin = async () => {
    if (!confirm) return;
    setJoining(confirm.id);
    try {
      await API.post(`/cohorts/${confirm.id}/join`);
      addToast(`You've joined ${confirm.name}! Welcome email sent.`, 'success');
      setTimeout(() => navigate('/student/dashboard'), 1200);
    } catch (err) {
      addToast(err.response?.data?.error || err.message || 'Failed to join cohort', 'error');
    } finally {
      setJoining(null);
      setConfirm(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background:'#F5F5F5' }}>

      {/* Header */}
      <div style={{ background:'#1A1A1A' }} className="px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-5 flex flex-col rounded-sm overflow-hidden border border-gray-600 flex-shrink-0">
              <div className="flex-1" style={{ background:'#1A1A1A' }} />
              <div className="flex-1" style={{ background:'#DD0000' }} />
              <div className="flex-1" style={{ background:'#FFCE00' }} />
            </div>
            <span className="text-sm font-bold text-white">
              <span style={{ color:'#FFCE00' }}>Deutsch</span>Bridge Institute
            </span>
          </div>
          <span className="text-xs text-gray-400">Step 3 of 3 — Choose your class</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background:'#fff2f2', border:'1px solid #fecaca' }}>
            <span className="text-xs font-bold" style={{ color:'#DD0000' }}>Level {level} — Paid ✓</span>
          </div>
          <h1 className="text-2xl font-bold text-de-black mb-2">Choose Your Class Time</h1>
          <p className="text-gray-500 text-sm">
            Select a cohort that fits your schedule. You'll receive Google Meet details by email.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
        ) : cohorts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center shadow-sm">
            <p className="text-3xl mb-3">🏫</p>
            <h3 className="text-base font-bold text-de-black mb-2">No cohorts available yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              The admin hasn't created cohorts for Level {level} yet.
              You'll be notified as soon as classes are scheduled.
            </p>
            <button onClick={() => navigate('/student/dashboard')}
              className="px-5 py-2.5 rounded-xl font-bold text-sm text-white"
              style={{ background:'#DD0000' }}>
              Go to Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {cohorts.map(c => {
              const enrolled  = c.enrolled_count || c.current_students || 0;
              const max       = c.max_students || 20;
              const full      = c.status === 'full' || enrolled >= max;
              const spotsLeft = Math.max(0, max - enrolled);
              const fillPct   = Math.min(100, (enrolled / max) * 100);

              return (
                <div key={c.id}
                  className={`bg-white rounded-2xl border-2 overflow-hidden shadow-sm transition-all
                    ${full ? 'border-gray-200 opacity-75' : 'border-gray-200 hover:border-brand-300 hover:shadow-lg'}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold capitalize text-white"
                            style={{ background: c.session === 'morning' ? '#FFCE00' : '#1A1A1A',
                                     color:      c.session === 'morning' ? '#1A1A1A' : '#fff' }}>
                            {c.session === 'morning' ? '🌅 Morning' : '🌆 Afternoon'}
                          </span>
                          {full && (
                            <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-500">
                              FULL
                            </span>
                          )}
                        </div>

                        <h3 className="font-bold text-de-black text-base mb-1">{c.name}</h3>

                        <div className="space-y-1.5 text-sm text-gray-600">
                          <p>📅 <span className="font-medium">{formatDays(c.days)}</span></p>
                          <p>⏰ <span className="font-medium">{formatTime(c.start_time)} — {formatTime(c.end_time)}</span></p>
                          {c.start_date && (
                            <p>🗓 Starts <span className="font-medium">{new Date(c.start_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}</span></p>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0 text-right">
                        <p className={`text-xl font-bold ${full ? 'text-gray-400' : 'text-de-black'}`}>
                          {enrolled}/{max}
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          {full ? 'No spots' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                        </p>
                        <button
                          disabled={full || joining === c.id}
                          onClick={() => !full && setConfirm(c)}
                          className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all
                            ${full
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'text-white hover:opacity-90 cursor-pointer'}`}
                          style={!full ? { background:'#DD0000' } : {}}>
                          {joining === c.id ? '…' : full ? 'Full' : 'Join →'}
                        </button>
                      </div>
                    </div>

                    {/* Fill bar */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all"
                          style={{ width:`${fillPct}%`, background: full ? '#9ca3af' : '#DD0000' }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Need help choosing? Call us at{' '}
          <span className="font-bold" style={{ color:'#DD0000' }}>0202071417</span>
        </p>
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-base font-bold text-de-black mb-1">Confirm Cohort Selection</h3>
            <p className="text-sm text-gray-600 mb-4">
              You're about to join <strong>{confirm.name}</strong>.
            </p>

            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-gray-500">Session</span>
                <span className="font-medium capitalize">{confirm.session}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Days</span>
                <span className="font-medium">{formatDays(confirm.days)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time</span>
                <span className="font-medium">{formatTime(confirm.start_time)} — {formatTime(confirm.end_time)}</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-4">
              A welcome email with your Google Meet link will be sent to your email address.
            </p>

            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleJoin} disabled={joining === confirm.id}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity disabled:opacity-60"
                style={{ background:'#DD0000' }}>
                {joining === confirm.id ? 'Joining…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
